import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { profileService } from '../../services/profileService';
import { skillService } from '../../services/skillService';
import './SkillManagement.css';

const SkillManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [profileSkills, setProfileSkills] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newSkill, setNewSkill] = useState({
    skill_id: '',
    proficiency_level: 'intermediate',
    years_of_experience: '',
    last_used_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileData, skillsData, allSkillsData] = await Promise.all([
        profileService.getProfile(id),
        profileService.getProfileSkills(id),
        skillService.getAllSkills()
      ]);
      setProfile(profileData);
      setProfileSkills(Array.isArray(skillsData) ? skillsData : (skillsData.data || []));
      setAllSkills(Array.isArray(allSkillsData) ? allSkillsData : (allSkillsData.data || []));
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    try {
      await profileService.addSkillToProfile(id, newSkill);
      setShowAddModal(false);
      setNewSkill({
        skill_id: '',
        proficiency_level: 'intermediate',
        years_of_experience: '',
        last_used_date: new Date().toISOString().split('T')[0]
      });
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to add skill');
    }
  };

  const handleRemoveSkill = async (skillId) => {
    if (!window.confirm('Remove this skill from profile?')) return;
    
    try {
      await profileService.removeSkillFromProfile(id, skillId);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to remove skill');
    }
  };

  const handleUpdateSkill = async (skillId, updates) => {
    try {
      await profileService.updateProfileSkill(id, skillId, updates);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update skill');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const availableSkills = allSkills.filter(
    skill => !profileSkills.find(ps => ps.skill_id === skill.id)
  );

  return (
    <div className="skill-management-container">
      <div className="skill-header">
        <div>
          <button onClick={() => navigate(`/profiles/${id}`)} className="btn-back">
            ← Back to Profile
          </button>
          <h1>Manage Skills</h1>
          <p className="profile-name">{profile?.first_name} {profile?.last_name}</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">
          + Add Skill
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="skills-list">
        {profileSkills.length > 0 ? (
          profileSkills.map(skill => (
            <SkillCard
              key={skill.id}
              skill={skill}
              onUpdate={handleUpdateSkill}
              onRemove={handleRemoveSkill}
            />
          ))
        ) : (
          <div className="no-skills">
            <p>No skills added yet</p>
            <button onClick={() => setShowAddModal(true)} className="btn-primary">
              Add Your First Skill
            </button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Add Skill</h2>
            <form onSubmit={handleAddSkill}>
              <div className="form-group">
                <label>Skill *</label>
                <select
                  value={newSkill.skill_id}
                  onChange={e => setNewSkill({...newSkill, skill_id: e.target.value})}
                  required
                >
                  <option value="">Select a skill...</option>
                  {availableSkills.map(skill => (
                    <option key={skill.id} value={skill.id}>
                      {skill.skill_name || skill.name} ({skill.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Proficiency Level *</label>
                <select
                  value={newSkill.proficiency_level}
                  onChange={e => setNewSkill({...newSkill, proficiency_level: e.target.value})}
                  required
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>

              <div className="form-group">
                <label>Years of Experience</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={newSkill.years_of_experience}
                  onChange={e => setNewSkill({...newSkill, years_of_experience: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Last Used Date</label>
                <input
                  type="date"
                  value={newSkill.last_used_date}
                  onChange={e => setNewSkill({...newSkill, last_used_date: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">Add Skill</button>
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const SkillCard = ({ skill, onUpdate, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    proficiency_level: skill.proficiency_level,
    years_of_experience: skill.years_of_experience || '',
    last_used_date: skill.last_used_date || ''
  });

  const handleSave = () => {
    onUpdate(skill.skill_id, editData);
    setIsEditing(false);
  };

  return (
    <div className="skill-card-manage">
      <div className="skill-card-header">
        <div>
          <h3>{skill.skill_name}</h3>
          <span className="skill-category">{skill.category}</span>
        </div>
        <div className="skill-actions">
          {!isEditing ? (
            <>
              <button onClick={() => setIsEditing(true)} className="btn-icon">✏️</button>
              <button onClick={() => onRemove(skill.skill_id)} className="btn-icon">🗑️</button>
            </>
          ) : (
            <>
              <button onClick={handleSave} className="btn-icon">✓</button>
              <button onClick={() => setIsEditing(false)} className="btn-icon">✕</button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="skill-edit-form">
          <div className="form-group-inline">
            <label>Level:</label>
            <select
              value={editData.proficiency_level}
              onChange={e => setEditData({...editData, proficiency_level: e.target.value})}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>
          <div className="form-group-inline">
            <label>Years:</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={editData.years_of_experience}
              onChange={e => setEditData({...editData, years_of_experience: e.target.value})}
            />
          </div>
          <div className="form-group-inline">
            <label>Last Used:</label>
            <input
              type="date"
              value={editData.last_used_date}
              onChange={e => setEditData({...editData, last_used_date: e.target.value})}
            />
          </div>
        </div>
      ) : (
        <div className="skill-details">
          <span className={`skill-level level-${skill.proficiency_level}`}>
            {skill.proficiency_level}
          </span>
          {skill.years_of_experience && (
            <span className="skill-info">{skill.years_of_experience} years</span>
          )}
          {skill.last_used_date && (
            <span className="skill-info">
              Last used: {new Date(skill.last_used_date).toLocaleDateString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillManagement;
